import { CONTENTS as ITEMS_CONTENTS } from '~/assets/collections/items';
import { CONTENTS as VEHICLES_CONTENTS } from '~/assets/collections/vehicles';
import { Subject } from '~/rage/utils';
import { ICharacterDonateItem, isClothesItem, RewardType } from '~/shared';
import { BackendService } from '../BackendService';
import { ICasesService } from '../CasesService';
import { GameConfigService } from '../GameConfigService';
import { IInventoryDonateService } from './inventoryDonateServiceTypes';

export class InventoryDonateService implements IInventoryDonateService {
	private donateItems = new Subject<ICharacterDonateItem[]>([]);
	private lootboxesIds = new Subject<string[]>([]);
	private bundlesIds = new Subject<string[]>([]);
	private itemsCount = new Subject<number>(0);

	constructor(
		private readonly backend: BackendService,
		private readonly casesService: ICasesService,
		private readonly gameConfig: GameConfigService,
	) {
		this.backend.addEventHandler(
			'donate:ownedItems',
			(data: { items: ICharacterDonateItem[] }) => {
				const { items } = data;
				if (!items) {
					this.donateItems.next([]);
					return;
				}
				this.donateItems.next(items);
			},
		);

		this.casesService.getLootboxes().subscribe((lootboxes) => {
			this.lootboxesIds.next(lootboxes.map((lootbox) => lootbox.id));
		});

		this.casesService.getBundles().subscribe((bundles) => {
			this.bundlesIds.next(bundles.map((bundle) => bundle.id));
		});

		this.donateItems.subscribe((items) => {
			this.itemsCount.next(items.length);
		});
	}

	public getDonateOwnedItems(): Subject<ICharacterDonateItem[]> {
		return this.donateItems;
	}

	public getTypeById(itemId: string): RewardType {
		const donateItem = this.gameConfig.configs?.donate?.inventoryItems.find(
			(item) => item.itemId === itemId,
		);
		if (donateItem) {
			return donateItem.rewardType;
		}

		if (
			ITEMS_CONTENTS.includes(itemId as (typeof ITEMS_CONTENTS)[number]) ||
			isClothesItem(itemId)
		) {
			return RewardType.ITEM;
		}

		if (
			VEHICLES_CONTENTS.includes(itemId as (typeof VEHICLES_CONTENTS)[number])
		) {
			return RewardType.VEHICLE;
		}

		if (this.lootboxesIds.getValue().includes(itemId)) {
			return RewardType.LOOTBOX;
		}

		if (this.bundlesIds.getValue().includes(itemId)) {
			return RewardType.BUNDLE;
		}

		return RewardType.ITEM;
	}

	public getItemsCount(): Subject<number> {
		return this.itemsCount;
	}

	public useReward(itemId: string): Promise<void> {
		return this.backend.invoke('donate:redeemItem', { itemId });
	}

	public sellReward(itemId: string): Promise<void> {
		return this.backend.invoke('donate:sellItem', { itemId });
	}

	public getPriceById(itemId: string): number | null {
		const donateItem = this.gameConfig.configs?.donate?.inventoryItems.find(
			(item) => item.itemId === itemId,
		);
		return donateItem?.sellAmount ?? null;
	}
}
